import { IProduct } from '@kizi/comparator-core';
import { Div, P } from '@kizi/core-components';
import { TimelineItemProps } from '@kizi/shared-components';
import React from 'react';
import { HOW_IT_WORKS } from '../../../../../funnels/home';
import { AfterTheSwitch } from '../../../../AfterTheSwitch';
import { ContractSwitchFaq } from '../../../../Product/ExpandedPart/ContractSwitchFaq';
import {
  getContractSwitchFaqMockData,
  getMockTimelineItems,
} from '../../../../Product/ExpandedPart/functions';
import { SwitchTab3P, SwitchTabOS3P } from './3P';
import { SwitchTabHome } from './Home';

interface SwitchTabProps {
  data: IProduct;
  isOs?: boolean;
  mainDescription?: string;
}

export const HowItWorks: React.FC<SwitchTabProps> = ({
  data,
  isOs,
  mainDescription,
}) => {
  const { provider, type } = data;
  const { name, howItWorks } = provider;

  if (!Array.isArray(howItWorks)) return null;

  if (!mainDescription) {
    if (['home', 'os-home', 'os-travel', 'travel'].includes(type)) {
      mainDescription = `Onderstaand zie je wat je kunt verwachten zodra je een ${name} verzekering hebt aangevraagd.`;
    } else if (type === 'savings') {
      mainDescription = `Onderstaand zie je wat kunt verwachten zodra je een bankrekening hebt aangevraagd bij ${name}`;
    } else if (type === 'solarPanels') {
      mainDescription = '';
    } else {
      mainDescription =
        'Beste deal gevonden? Hieronder lees je wat je kunt verwachten na het aanvragen van je verzekering';
    }
  }

  function getSteps(step): TimelineItemProps {
    switch (step.typeName) {
      case HOW_IT_WORKS.AFFILIATE:
        return {
          title: 'Aanvraag op de website van verzekeraar',
          body: step.value,
        };
      case HOW_IT_WORKS.CONFIRMATION_COMPANY:
        return {
          title: `Bevestiging ${name}`,
          body: step.value,
        };
      case HOW_IT_WORKS.CONFIRMATION_CB:
        return {
          title: 'Bevestiging Consumentenbond',
          body: step.value,
        };
      case HOW_IT_WORKS.CANCEL_OLD:
        return {
          title: 'Opzeggen oude verzekering',
          body: step.value,
        };
      case HOW_IT_WORKS.ORDER:
        return {
          title: 'Aanvraag indienen',
          body: step.value,
        };
      case HOW_IT_WORKS.CHECK:
        return {
          title: 'Technische schouw',
          body: step.value,
        };
      case HOW_IT_WORKS.PLAN:
        return {
          title: 'Planning',
          body: step.value,
        };
      case HOW_IT_WORKS.INSTALLATION:
        return {
          title: 'Installatiedag',
          body: step.value,
        };

      case HOW_IT_WORKS.SERVICE:
        return {
          title: 'Service',
          body: step.value,
        };
      default:
        return { title: '', body: '' };
    }
  }

  function getOverstappenSteps(step): TimelineItemProps {
    switch (step.typeName) {
      case HOW_IT_WORKS.AFFILIATE:
        return {
          title:
            type === 'savings'
              ? 'Aanvraag op de website van de bank'
              : 'Aanvraag op de website van verzekeraar',
          body: step.value,
        };
      case HOW_IT_WORKS.CONFIRMATION_COMPANY:
        return {
          title: `Bevestiging ${name}`,
          body: step.value,
        };
      case HOW_IT_WORKS.CONFIRMATION_CB:
        return {
          title: 'Bevestiging Overstappen',
          body: step.value,
        };
      case HOW_IT_WORKS.CANCEL_OLD:
        return {
          title: 'Opzeggen oude verzekering',
          body: step.value,
        };
      default:
        return { title: '', body: '' };
    }
  }

  return (
    <AfterTheSwitch
      title={type === 'solarPanels' ? 'Stappenplan' : 'Zo werkt je aanvraag'}
      subtitle={mainDescription}
      steps={[
        ...howItWorks.map((step) => {
          return isOs ? getOverstappenSteps(step) : getSteps(step);
        }),
      ]}
    />
  );
};

export const SwitchTab: React.FC<SwitchTabProps> = ({ data, isOs }) => {
  const { provider, type } = data;
  const { name, switchWindowDays, taskDivisions, howItWorks } = provider;

  if (type === 'tripleplay') {
    return <SwitchTab3P supplier={undefined} />;
  } else if (type === 'os-triple-play') {
    return (
      <SwitchTabOS3P
        supplier={undefined}
        averageDeliveryTimeInDays={data?.averageDeliveryTimeInDays}
      />
    );
  } else {
    return (
      <Div display={[null, null, 'flex']} variant="productTabSwitch">
        <Div className="tab-section" width={'50%'}>
          {(switchWindowDays || type === 'energyAuction') && (
            <>
              <P variant="bodyImportant">{`Overstappen naar ${name}`}</P>
              <P variant="footnoteRegular">
                {`Als je overstapt, zegt ${name} het contract bij je huidige energieleverancier op.`}
              </P>
              <ContractSwitchFaq
                items={getContractSwitchFaqMockData(
                  name,
                  switchWindowDays,
                  type,
                  taskDivisions,
                )}
              />
            </>
          )}
          {type === 'solarPanels' && (
            <>
              <P variant="bodyImportant">{'Informatie'}</P>
              <P variant="footnoteRegularLightGrey" fontWeight="300">
                {
                  'Heb je de juiste aanbieding voor jou gevonden? Dan kun je deze gemakkelijk aanvragen via de website. Wanneer je de aanvraag hebt afgerond, zal je een bevestiging krijgen per e-mail. Daarna wordt je telefonisch of per e-mail benaderd door de leverancier. Zij gaan alle verstrekte informatie met je doornemen en eventueel de technische schouw inplannen. Nadat alle informatie is verzameld zal er een datum worden vastgesteld. Op deze dag zullen jouw zonnepanelen worden geplaatst. Nadat de zonnepanelen zijn geplaatst moeten deze worden betaald.'
                }
              </P>
              <P variant="bodyImportant" mt={4} mb="0px!important">
                {'Veelgestelde vragen'}
              </P>
              <Div variant="divider" my={1} />
              <ContractSwitchFaq
                items={getContractSwitchFaqMockData(
                  name,
                  switchWindowDays,
                  type,
                  taskDivisions,
                )}
              />
              <Div variant="divider" my={1} />
            </>
          )}
          {['home', 'travel'].includes(type) && <SwitchTabHome data={data} />}
        </Div>
        {Array.isArray(howItWorks) ? (
          <HowItWorks data={data} isOs={isOs} />
        ) : (
          <AfterTheSwitch
            title="Zo werkt je aanvraag"
            subtitle="Hieronder kun je lezen wat je kunt verwachten na het aanvragen van je energiecontract."
            steps={getMockTimelineItems(type, name)}
          />
        )}
      </Div>
    );
  }
};

export { SwitchTabHome } from './Home';
export { SwitchTab3P, SwitchTabOS3P } from './3P';
